import { useState, useRef, useEffect, useCallback } from 'react'
import {
  Send,
  Square,
  Paperclip,
  X,
  Loader2,
  AlertCircle,
  FileText,
  Image as ImageIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  useUploadAgentAttachment,
  useDeleteAgentAttachment,
  type UploadedAgentAttachment,
} from '@/hooks/use-agent-conversations'
import type { SendAttachment } from '@/hooks/use-agent-chat'
import { ComposeTextarea } from '@/components/compose-field'
import { getApiErrorMessage } from '@/lib/api-error'

// Mirror backend MAX_UPLOAD_BYTES so the user gets immediate feedback
// instead of waiting for a 413 from the API.
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024

const ACCEPTED_FILE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'text/plain',
  'text/markdown',
  'application/json',
].join(',')

interface PendingAttachment {
  /** Local-only id used while the upload is in flight. */
  localId: string
  file: File
  status: 'uploading' | 'ready' | 'error'
  error?: string
  /** Populated once the upload succeeds. */
  uploaded?: UploadedAgentAttachment
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(2)} MB`
}

interface Props {
  onSend: (text: string, attachments?: SendAttachment[]) => Promise<boolean>
  onCancel: () => void
  disabled?: boolean
  isStreaming: boolean
  placeholder?: string
  /** Required for uploads. When null, the file picker is hidden. */
  conversationUuid: string | null
}

export function AgentComposer({
  onSend,
  onCancel,
  disabled,
  isStreaming,
  placeholder,
  conversationUuid,
}: Props) {
  const [value, setValue] = useState('')
  const [pending, setPending] = useState<PendingAttachment[]>([])
  const [dragActive, setDragActive] = useState(false)
  const ref = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const submissionPendingRef = useRef(false)

  const upload = useUploadAgentAttachment()
  const remove = useDeleteAgentAttachment()

  // Auto-grow textarea
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, window.innerHeight * 0.4) + 'px'
  }, [value])

  const startUpload = useCallback(
    (files: File[]) => {
      if (!conversationUuid) return
      for (const file of files) {
        const localId = `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 8)}`
        if (file.size > MAX_UPLOAD_BYTES) {
          setPending((prev) => [
            ...prev,
            {
              localId,
              file,
              status: 'error',
              error: `Too large (${formatBytes(file.size)} > ${formatBytes(MAX_UPLOAD_BYTES)})`,
            },
          ])
          continue
        }
        setPending((prev) => [...prev, { localId, file, status: 'uploading' }])
        upload.mutate(
          { conversationUuid, file },
          {
            onSuccess: (uploaded) => {
              setPending((prev) =>
                prev.map((p) =>
                  p.localId === localId ? { ...p, status: 'ready', uploaded } : p,
                ),
              )
            },
            onError: (err) => {
              const msg = getApiErrorMessage(err, 'Upload failed')
              setPending((prev) =>
                prev.map((p) =>
                  p.localId === localId ? { ...p, status: 'error', error: msg } : p,
                ),
              )
            },
          },
        )
      }
    },
    [conversationUuid, upload],
  )

  const removeAttachment = useCallback(
    (localId: string) => {
      const target = pending.find((p) => p.localId === localId)
      setPending((prev) => prev.filter((p) => p.localId !== localId))
      if (target?.uploaded && conversationUuid) {
        remove.mutate({
          conversationUuid,
          attachmentUuid: target.uploaded.uuid,
        })
      }
    },
    [pending, conversationUuid, remove],
  )

  const submit = async () => {
    if (disabled || isStreaming || submissionPendingRef.current) return
    const submittedDraft = value
    const text = value.trim()
    const readyItems = pending.filter((p) => p.status === 'ready' && p.uploaded)
    if (!text && readyItems.length === 0) return
    // Don't send while any upload is still in-flight; the user can hit
    // send again once everything's ready.
    if (pending.some((p) => p.status === 'uploading')) return
    const submittedAttachmentIds = new Set(readyItems.map((item) => item.localId))
    submissionPendingRef.current = true
    try {
      const accepted = await onSend(
        text,
        readyItems.map((item): SendAttachment => {
          const uploaded = item.uploaded!
          return {
            attachment_uuid: uploaded.uuid,
            filename: uploaded.filename,
            content_type: uploaded.content_type,
            size_bytes: uploaded.size_bytes,
            is_image: uploaded.is_image,
            is_pdf: uploaded.is_pdf,
          }
        }),
      )
      if (!accepted) return

      setValue((current) => {
        if (current === submittedDraft) return ''
        if (submittedDraft && current.startsWith(submittedDraft)) {
          return current.slice(submittedDraft.length)
        }
        return current
      })
      setPending((current) =>
        current.filter((item) => !submittedAttachmentIds.has(item.localId)),
      )
    } catch {
      // The hook reports authenticated emission failures as false. Preserve
      // the draft if another caller unexpectedly rejects as well.
    } finally {
      submissionPendingRef.current = false
    }
  }

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void submit()
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length > 0) startUpload(files)
    // Reset so picking the same file twice triggers onChange again.
    e.target.value = ''
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!conversationUuid) return
    e.preventDefault()
    setDragActive(true)
  }
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragActive(false)
  }
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragActive(false)
    if (!conversationUuid) return
    const files = Array.from(e.dataTransfer.files ?? [])
    if (files.length > 0) startUpload(files)
  }

  const anyUploading = pending.some((p) => p.status === 'uploading')
  const hasContent = value.trim().length > 0 || pending.some((p) => p.status === 'ready')
  const canAttach = !!conversationUuid && !disabled

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        'border-t bg-background/90 backdrop-blur px-3 py-2',
        dragActive && 'ring-2 ring-primary/40 ring-inset',
      )}
    >
      {pending.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {pending.map((p) => (
            <AttachmentChip key={p.localId} item={p} onRemove={() => removeAttachment(p.localId)} />
          ))}
        </div>
      ) : null}
      <div className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_FILE_TYPES}
          onChange={handleFileChange}
          className="hidden"
        />
        <Button
          size="icon"
          variant="ghost"
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={!canAttach}
          aria-label="Attach file"
          className="size-11 shrink-0 sm:size-9 [@media(pointer:coarse)]:size-11"
        >
          <Paperclip className="h-4 w-4" />
        </Button>
        <ComposeTextarea
          ref={ref}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKey}
          placeholder={placeholder ?? 'Ask anything…'}
          className="min-h-[40px] resize-none border-muted-foreground/20 text-sm focus-visible:ring-1 placeholder:text-sm"
          disabled={disabled && !isStreaming}
          rows={1}
        />
        {isStreaming ? (
          <Button
            size="icon"
            variant="outline"
            onClick={onCancel}
            aria-label="Stop"
            className="size-11 sm:size-9 [@media(pointer:coarse)]:size-11"
          >
            <Square className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            size="icon"
            onClick={() => void submit()}
            disabled={disabled || !hasContent || anyUploading}
            aria-label="Send"
            className="size-11 sm:size-9 [@media(pointer:coarse)]:size-11"
          >
            <Send className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="mt-1 px-1 text-[10px] text-muted-foreground">
        Enter to send · Shift+Enter for newline · Drop files to attach (PDF, image, Word, Excel, CSV, txt — max{' '}
        {formatBytes(MAX_UPLOAD_BYTES)})
      </div>
    </div>
  )
}

function AttachmentChip({
  item,
  onRemove,
}: {
  item: PendingAttachment
  onRemove: () => void
}) {
  const isImage =
    item.uploaded?.is_image ?? (item.file.type ? item.file.type.startsWith('image/') : false)
  const isPdf = item.uploaded?.is_pdf ?? item.file.type === 'application/pdf'
  const Icon = isImage ? ImageIcon : isPdf ? FileText : Paperclip
  const size = item.uploaded?.size_bytes ?? item.file.size

  return (
    <div
      className={cn(
        'inline-flex max-w-full items-center gap-1.5 rounded-md border px-2 py-1 text-xs',
        item.status === 'error'
          ? 'border-destructive/50 bg-destructive/5'
          : 'border-muted-foreground/20 bg-muted/40',
      )}
      title={item.error || item.file.name}
    >
      {item.status === 'uploading' ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
      ) : item.status === 'error' ? (
        <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
      ) : (
        <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      )}
      <span className="max-w-[14rem] truncate font-medium">{item.file.name}</span>
      <span className="text-[10px] text-muted-foreground">{formatBytes(size)}</span>
      {item.status === 'error' && item.error ? (
        <span className="text-[10px] text-destructive">· {item.error}</span>
      ) : null}
      <button
        type="button"
        onClick={onRemove}
        className="ml-0.5 flex size-11 items-center justify-center rounded text-muted-foreground hover:bg-background hover:text-foreground sm:size-9 [@media(pointer:coarse)]:size-11"
        aria-label={`Remove ${item.file.name}`}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  )
}
